import React, { useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { groupBy } from 'lodash';
import {
    MenuContentProps,
    cssVar,
    styled,
    MenuSubTrigger,
    MenuItem,
    Menu,
    MenuContent,
    MenuSeparator,
    MenuSub,
    MenuSubContent,
    MenuTrigger,
    Text,
    Box,
} from '@cad-arc';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useBroadcast } from '../../common/broadcast-channel/use-broadcast';
import { useElectronWindows } from '../../electron-windows/use-electron-windows';
import { DexRegionEnum, SystemResource, WorkspaceWindowTypeEnumType } from '@mark43/cad-api';
import { useOpenDepartmentLink } from '../use-open-department-link';
import { useGenerateRepossession } from './use-generate-repossession';
import { useFullscreenModalControls } from '../../modals/context/modal-context-provider';
import { useOpenUnitHistoryPanel } from '../../unit-history/hooks/use-open-unit-history-panel';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useApiClient } from '@m43/core';
import { getErrorReportScreenshots } from './submit-error-report-modal';
import { usePanelsPermittedInWindow } from '../../common/hooks/use-panels-permitted-in-window';
import { useIntegratedActionBroadcaster } from '../../action-broadcaster/use-integrated-action-broadcaster';
import { v4 } from 'uuid';
import { useApplicationType } from '../../common/hooks/use-application-type';
import {
    FR_CONTACT_LIST_PATH,
    FR_EDIT_UNIT_PATH,
    FR_UNIT_HISTORY_PATH,
    ROOT_PATH,
    UNIT_STATUS_SEARCH_WINDOW_PATH,
} from '../../router/constants';
import { WorkspaceManagementMenuContent } from '../../workspace-management/workspace-management-menu';
import { useUnitAssignment } from '../../unit-assignment/unit-assignment-context';
import { OffDutyItem } from '../../common/components/off-duty-item';
import { useQuit } from '../../common/hooks/use-quit';
import { useLogout } from '../../common/hooks/use-logout';
import { useQuery } from '@tanstack/react-query';
import { electronBridge } from '../../electron-bridge/electron-bridge';
import { useGetDisplayedShortcutKeyByCommand } from '../../fr-navigation/components/use-shortcut-key-by-command';
import { MaLeapsPassword } from '../../data-exchange/components/ma-leaps-password';
import { useDexPasswordApi } from '../../data-exchange/use-dex-password-api';
import { FrExportsMenuItem } from '../../fr-navigation/components/fr-exports-menu-item';
import { FrExportsMenuItemProps } from './types';
import { useWorkspaceStore } from '../../panels/use-workspace-store';
import { Tooltip } from '~/common/components/tooltip';

const EnlargedMenuSubTrigger = styled(MenuSubTrigger)`
    font-size: ${cssVar('arc.fontSizes.lg')};
    height: ${cssVar('arc.sizes.12')};
`;

function HamburgerMenuContent({
    frExportsMenuItemProps,
}: {
    frExportsMenuItemProps?: FrExportsMenuItemProps;
}): React.ReactElement {
    const {
        isCad,
        isCadElectron,
        isCadWeb,
        isElectron,
        isElectronRewrite,
        isFirstResponder,
        isFirstResponderWeb,
    } = useApplicationType();
    const logout = useLogout();
    const quit = useQuit();
    const { trigger } = useIntegratedActionBroadcaster();
    const { postMessage } = useBroadcast('legacy-store-broadcast');
    const apiClient = useApiClient();
    const openDepartmentLink = useOpenDepartmentLink();
    const modalControls = useFullscreenModalControls();
    const bootstrapView = useBootstrapState();
    const enabledFeatures = useEnabledFeatures();
    const electronWindows = useElectronWindows();
    const unitAssignment = useUnitAssignment();
    const generateRepossession = useGenerateRepossession();
    const openUnitHistoryPanel = useOpenUnitHistoryPanel();
    const panelsPermittedInWindow = usePanelsPermittedInWindow();

    const showCadNextUnitHistoryPanelButton =
        enabledFeatures.unitHistory && panelsPermittedInWindow && isCad;

    const showFireStationInformationButton =
        enabledFeatures.responsePlanCrossStaffingEnabled && isCad;

    const newElectronWorkspaceWindowsQuery = useQuery(
        ['workspace-windows'],
        electronBridge?.requestWorkspaceWindows ?? (() => {}),
        {
            enabled: !!(electronBridge && 'isElectronRewrite' in electronBridge),
        }
    );
    const newElectronWorkspaceWindows = newElectronWorkspaceWindowsQuery.data;
    const leapsPasswordInfo = useDexPasswordApi();
    const showMaLeapsPasswordOption =
        isFirstResponder && bootstrapView?.dexRegion === DexRegionEnum.MA_LEAPS.name;

    const workspaceWindowsByType = React.useMemo((): Partial<
        Record<WorkspaceWindowTypeEnumType, unknown[]>
    > => {
        return groupBy(
            electronBridge && 'isElectronRewrite' in electronBridge
                ? newElectronWorkspaceWindows ?? []
                : electronWindows,
            'workspaceWindowType'
        );
    }, [electronWindows, newElectronWorkspaceWindows]);

    const isWindowOpen = useCallback(
        (workspaceWindowType: WorkspaceWindowTypeEnumType) => {
            return (workspaceWindowsByType[workspaceWindowType] ?? []).length > 0;
        },
        [workspaceWindowsByType]
    );

    const getDisplayedShortcutKeyByCommand = useGetDisplayedShortcutKeyByCommand();
    const workspacePanelsStore = useWorkspaceStore();

    return (
        <>
            {isFirstResponder && (
                <>
                    <MenuSub>
                        <Tooltip overlay={workspacePanelsStore.workspaceName}>
                            <EnlargedMenuSubTrigger>
                                <Box width={276}>
                                    <Text
                                        style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textWrap: 'nowrap',
                                        }}
                                    >
                                        Workspaces: {workspacePanelsStore.workspaceName}
                                    </Text>
                                </Box>
                            </EnlargedMenuSubTrigger>
                        </Tooltip>
                        <MenuSubContent>
                            <WorkspaceManagementMenuContent />
                        </MenuSubContent>
                    </MenuSub>
                    <NavLink
                        style={{ textDecoration: 'none' }}
                        to={unitAssignment?.unitId ? FR_EDIT_UNIT_PATH : ROOT_PATH}
                    >
                        <MenuItem
                            leadingVisual="Edit"
                            trailingVisual={
                                <Text color="text.placeholder">
                                    {getDisplayedShortcutKeyByCommand('EDIT_UNIT')}
                                </Text>
                            }
                        >
                            Edit Unit
                        </MenuItem>
                    </NavLink>
                    {enabledFeatures.viewContactList && (
                        <NavLink style={{ textDecoration: 'none' }} to={FR_CONTACT_LIST_PATH}>
                            <MenuItem leadingVisual="ContactList">Contact List</MenuItem>
                        </NavLink>
                    )}
                    <NavLink style={{ textDecoration: 'none' }} to={UNIT_STATUS_SEARCH_WINDOW_PATH}>
                        <MenuItem
                            leadingVisual="SearchUnitStatuses"
                            trailingVisual={
                                <Text color="text.placeholder">
                                    {getDisplayedShortcutKeyByCommand('CAD_SEARCH')}
                                </Text>
                            }
                        >
                            Unit Status Search
                        </MenuItem>
                    </NavLink>
                    <NavLink style={{ textDecoration: 'none' }} to={FR_UNIT_HISTORY_PATH}>
                        <MenuItem leadingVisual="History">Unit History</MenuItem>
                    </NavLink>
                    {frExportsMenuItemProps && <FrExportsMenuItem {...frExportsMenuItemProps} />}
                    {enabledFeatures.firstResponderOffDuty &&
                        !!unitAssignment?.unitId &&
                        !!unitAssignment?.unitStateData?.id && (
                            <OffDutyItem
                                unitId={unitAssignment?.unitId}
                                unitStateId={unitAssignment?.unitStateData?.id}
                                isAssignedToEvent={!!unitAssignment?.assignedEvent}
                            />
                        )}
                </>
            )}
            {showCadNextUnitHistoryPanelButton && (
                <MenuItem leadingVisual="History" onSelect={() => openUnitHistoryPanel()}>
                    Unit History
                </MenuItem>
            )}

            {showFireStationInformationButton && (
                <MenuItem
                    onSelect={() =>
                        modalControls.open({ type: 'FIRE_STATION_INFO_MODAL', props: {} })
                    }
                >
                    Fire Station Information
                </MenuItem>
            )}

            {!isFirstResponder && (
                <>
                    {enabledFeatures.unitBulkLogonAdmin && (
                        <MenuItem
                            trailingVisual="OpenNewWindow"
                            onSelect={() => {
                                openDepartmentLink({
                                    app: 'RMS',
                                    path: 'admin/cad/units/logon/bulk',
                                });
                            }}
                        >
                            Log On Bulk Units
                        </MenuItem>
                    )}
                    {enabledFeatures.unitsAdmin && (
                        <MenuItem
                            trailingVisual="OpenNewWindow"
                            onSelect={() => {
                                openDepartmentLink({
                                    app: 'RMS',
                                    path: 'admin/cad/units',
                                });
                            }}
                        >
                            Units Admin
                        </MenuItem>
                    )}
                    {enabledFeatures.unitsAdmin && (
                        <MenuItem
                            trailingVisual="OpenNewWindow"
                            onSelect={() => {
                                openDepartmentLink({
                                    app: 'RMS',
                                    path: 'admin',
                                });
                            }}
                        >
                            CAD Admin Settings
                        </MenuItem>
                    )}
                </>
            )}
            <MenuSeparator />
            <MenuItem
                onSelect={() => {
                    setTimeout(() => {
                        // capture screenshots before opening the modal so that the modal doesn't appear
                        // in the screenshots
                        getErrorReportScreenshots().then((result) => {
                            modalControls.open({
                                type: 'SUBMIT_ERROR_REPORT',
                                props: { screenshots: result },
                            });
                        });
                        // delay by 50ms so that the menu has time to close before the screenshots are taken
                    }, 50);
                }}
            >
                Submit Error Report
            </MenuItem>
            <MenuItem
                onSelect={() => {
                    modalControls.open({
                        type: 'SHORTCUT_KEYS',
                        props: {},
                    });
                }}
            >
                List of Shortcut Keys
            </MenuItem>
            {isFirstResponder && (
                <>
                    <MenuItem
                        onSelect={() => {
                            modalControls.open({
                                type: 'PREFERENCES',
                                props: {},
                            });
                        }}
                    >
                        Preferences
                    </MenuItem>
                </>
            )}

            {showMaLeapsPasswordOption && <MaLeapsPassword leapsPasswordInfo={leapsPasswordInfo} />}
            {isCadElectron && (
                <>
                    <MenuSeparator />
                    <MenuItem
                        leadingVisual="Add"
                        onSelect={() => {
                            if (isElectronRewrite) {
                                trigger([
                                    {
                                        id: v4(),
                                        targetWindowType: 'ROOT',
                                        type: 'OPEN_QUEUE_V2_WINDOW',
                                    },
                                ]);
                            } else {
                                postMessage({ type: 'OPEN_QUEUE_V2_WINDOW' });
                            }
                        }}
                    >
                        Queues Window
                    </MenuItem>

                    {(workspaceWindowsByType['QUEUE_V2'] ?? []).length > 0 && (
                        <MenuItem leadingVisual="Check">
                            Queues{' '}
                            {workspaceWindowsByType['QUEUE_V2']!.length > 1
                                ? `(${workspaceWindowsByType['QUEUE_V2']?.length})`
                                : ''}
                        </MenuItem>
                    )}

                    <MenuItem
                        leadingVisual={
                            (workspaceWindowsByType['EVENT_V2'] ?? []).length > 0
                                ? 'Check'
                                : undefined
                        }
                        onSelect={() => {
                            if (isElectronRewrite) {
                                trigger([
                                    {
                                        id: v4(),
                                        targetWindowType: 'ROOT',
                                        type: 'OPEN_EVENT_V2_WINDOW',
                                    },
                                    {
                                        id: v4(),
                                        targetWindowType: 'EVENT_V2',
                                        type: 'FOCUS_ACTIVE_EVENT_WINDOW_PANEL',
                                    },
                                ]);
                            } else {
                                postMessage({ type: 'OPEN_EVENT_V2_WINDOW' });
                            }
                        }}
                    >
                        Events
                    </MenuItem>

                    {isElectronRewrite || enabledFeatures.disableLegacyMapAndSearches ? null : (
                        <MenuItem
                            leadingVisual={
                                (workspaceWindowsByType['AVL'] ?? []).length > 0
                                    ? 'Check'
                                    : undefined
                            }
                            onSelect={() => {
                                postMessage({ type: 'OPEN_MAP_WINDOW' });
                            }}
                        >
                            Legacy Map
                        </MenuItem>
                    )}

                    {(isElectronRewrite ||
                        enabledFeatures.disableLegacyMapAndSearches ||
                        enabledFeatures.mapWindowEnabled) && (
                        <MenuItem
                            leadingVisual={isWindowOpen('MAP_V2') ? 'Check' : undefined}
                            onSelect={() => {
                                if (isElectronRewrite) {
                                    trigger([
                                        {
                                            id: v4(),
                                            targetWindowType: 'ROOT',
                                            type: 'OPEN_MAP_V2_WINDOW',
                                        },
                                    ]);
                                } else {
                                    postMessage({ type: 'OPEN_MAP_V2_WINDOW' });
                                }
                            }}
                        >
                            Map
                        </MenuItem>
                    )}

                    {isElectronRewrite || enabledFeatures.disableLegacyMapAndSearches ? null : (
                        <MenuItem
                            leadingVisual={isWindowOpen('CAD_SEARCH') ? 'Check' : undefined}
                            onSelect={() => {
                                postMessage({ type: 'OPEN_EVENT_SEARCH_WINDOW' });
                            }}
                        >
                            Legacy Event Search
                        </MenuItem>
                    )}

                    {isElectronRewrite ||
                    enabledFeatures.disableLegacyMapAndSearches ||
                    enabledFeatures.searchWindowsEnabled ? (
                        <MenuItem
                            leadingVisual={isWindowOpen('EVENT_SEARCH_V2') ? 'Check' : undefined}
                            onSelect={() => {
                                if (isElectronRewrite) {
                                    trigger([
                                        {
                                            id: v4(),
                                            targetWindowType: 'ROOT',
                                            type: 'OPEN_EVENT_SEARCH_V2_WINDOW',
                                        },
                                    ]);
                                } else {
                                    postMessage({ type: 'OPEN_EVENT_SEARCH_V2_WINDOW' });
                                }
                            }}
                        >
                            Event Search
                        </MenuItem>
                    ) : null}

                    {isElectronRewrite || enabledFeatures.disableLegacyMapAndSearches ? null : (
                        <MenuItem
                            leadingVisual={isWindowOpen('UNIT_STATUS_SEARCH') ? 'Check' : undefined}
                            onSelect={() => {
                                postMessage({ type: 'OPEN_UNIT_STATUS_SEARCH_WINDOW' });
                            }}
                        >
                            Legacy Unit Status Search
                        </MenuItem>
                    )}

                    {(isElectronRewrite ||
                        enabledFeatures.disableLegacyMapAndSearches ||
                        enabledFeatures.unitStatusSearchWindowEnabled) && (
                        <MenuItem
                            leadingVisual={
                                isWindowOpen('UNIT_STATUS_SEARCH_V2') ? 'Check' : undefined
                            }
                            onSelect={() => {
                                if (isElectronRewrite) {
                                    trigger([
                                        {
                                            id: v4(),
                                            targetWindowType: 'ROOT',
                                            type: 'OPEN_UNIT_STATUS_SEARCH_V2_WINDOW',
                                        },
                                    ]);
                                } else {
                                    postMessage({ type: 'OPEN_UNIT_STATUS_SEARCH_V2_WINDOW' });
                                }
                            }}
                        >
                            Unit Status Search
                        </MenuItem>
                    )}
                    {isElectronRewrite && enabledFeatures.searchBolos && (
                        <MenuItem
                            leadingVisual={isWindowOpen('BOLO_SEARCH') ? 'Check' : undefined}
                            onSelect={() => {
                                trigger([
                                    {
                                        id: v4(),
                                        targetWindowType: 'ROOT',
                                        type: 'OPEN_BOLO_SEARCH_WINDOW',
                                    },
                                ]);
                            }}
                        >
                            BOLO Search
                        </MenuItem>
                    )}
                </>
            )}
            <MenuSeparator />
            {isCadWeb ? (
                <MenuItem
                    trailingVisual="Download"
                    onSelect={() => {
                        apiClient
                            .request<SystemResource.GetElectronDownloadUrl>({
                                method: 'GET',
                                url: 'system/download',
                                params: {
                                    platform: 'WINDOWS',
                                },
                            })
                            .then((response) => {
                                const downloadUrl = response.data.data;
                                if (downloadUrl) {
                                    window.open(downloadUrl);
                                }
                            });
                    }}
                >
                    Download Mark43 CAD
                </MenuItem>
            ) : undefined}
            {isFirstResponderWeb ? (
                <MenuItem
                    trailingVisual="Download"
                    onSelect={() => {
                        window.open(
                            'https://m43-fr-electron.s3.us-gov-west-1.amazonaws.com/latest/Mark43+First+Responder+Setup.exe'
                        );
                    }}
                >
                    Download Mark43 First Responder
                </MenuItem>
            ) : undefined}

            {enabledFeatures.canGenerateRepossessionRen && bootstrapView.userHasAccountInRmsTenant && (
                <MenuItem trailingVisual="OpenNewWindow" onSelect={generateRepossession}>
                    Generate Repossession
                </MenuItem>
            )}
            {enabledFeatures.firstResponderCore && isCad && (
                <MenuItem
                    trailingVisual="OpenNewWindow"
                    onSelect={() => {
                        openDepartmentLink({
                            path: '',
                            app: 'FIRST_RESPONDER_NEXT',
                        });
                    }}
                >
                    Open Mark43 First Responder
                </MenuItem>
            )}
            <MenuItem
                trailingVisual="OpenNewWindow"
                onSelect={() => {
                    openDepartmentLink({
                        path: '',
                        app: 'RMS',
                        tenantStrategy: 'PREFER_RMS_TENANT',
                    });
                }}
            >
                Open Mark43 RMS
            </MenuItem>
            {isFirstResponder && (
                <MenuItem
                    trailingVisual="OpenNewWindow"
                    onSelect={() => {
                        openDepartmentLink({
                            path: '',
                            app: 'CAD',
                        });
                    }}
                >
                    Open Mark43 CAD
                </MenuItem>
            )}
            {isCad && (
                <MenuItem
                    trailingVisual="OpenNewWindow"
                    onSelect={() => {
                        const urlToOpen = 'https://get.mark43.help/hc/s/';
                        if (electronBridge) {
                            electronBridge.openUrlInBrowser(urlToOpen);
                        } else {
                            window.open(urlToOpen);
                        }
                    }}
                >
                    Support
                </MenuItem>
            )}
            {enabledFeatures.sentryTestEnabled && (
                <MenuItem
                    trailingVisual="Rejected"
                    onSelect={() => {
                        throw new Error('Test error for Sentry');
                    }}
                >
                    Trigger Error
                </MenuItem>
            )}
            <MenuItem onSelect={logout}>Log Out</MenuItem>
            {isElectron && <MenuItem onSelect={quit}>Quit</MenuItem>}
        </>
    );
}

export function HamburgerMenu(props: {
    trigger: React.ReactNode;
    side?: MenuContentProps['side'];
    frExportsMenuItemProps?: FrExportsMenuItemProps;
}): React.ReactElement {
    return (
        <Menu>
            <MenuTrigger asChild>{props.trigger}</MenuTrigger>
            <MenuContent align="start" collisionPadding={0} side={props.side}>
                <HamburgerMenuContent frExportsMenuItemProps={props.frExportsMenuItemProps} />
            </MenuContent>
        </Menu>
    );
}
